import React, { useState } from 'react';
import { Clock, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import { generateTimelineThunk } from '../../../redux/thunks/TimelineThunks';
import { DBTimelineThunks } from '../../../redux/thunks/DBServiceThunks';
import { selectCanEditCurrentCase } from '../../../redux/selectors/knowledgebaseSelectors';

interface TimelineEmptyStateProps {
  knowledgebaseId: string;
}

export default function TimelineEmptyState({ knowledgebaseId }: TimelineEmptyStateProps) {
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentCaseId = useAppSelector(state => state.CaseReducer.currentCaseId);
  const currentLanguage = i18n.language || 'en';

  // Check if user can edit
  const canEdit = useAppSelector(selectCanEditCurrentCase);

  const handleGenerateTimeline = async () => {
    if (!currentCaseId || !knowledgebaseId) {
      setError(t('common.labels.error'));
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const result = await dispatch(generateTimelineThunk({
        knowledgebaseId,
        caseId: currentCaseId,
        language: currentLanguage
      })).unwrap();

      console.log('Timeline generation started:', result);

      // Start polling for the timeline
      setTimeout(() => {
        dispatch(DBTimelineThunks.find({ knowledgebaseId }));
      }, 2000);

    } catch (err: any) {
      console.error('Failed to generate timeline:', err);
      setError(err || t('common.labels.error'));
      setIsGenerating(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center h-full p-8 text-center">
      <Clock className="h-16 w-16 text-muted-foreground mb-4" />

      <h3 className="text-lg font-semibold text-foreground mb-2">
        {t('common.timeline.noTimelineYet')}
      </h3>

      <p className="text-sm text-muted-foreground mb-6 max-w-sm">
        {t('common.timeline.generateDescription')}
      </p>

      {error && (
        <p className="text-sm text-destructive mb-4">
          {error}
        </p>
      )}

      <Button
        onClick={handleGenerateTimeline}
        disabled={isGenerating || !canEdit}
        className="gap-2"
      >
        {isGenerating && <Loader2 className="h-4 w-4 animate-spin" />}
        {isGenerating ? t('common.timeline.generating') : t('common.timeline.generateTimeline')}
      </Button>

      {isGenerating && (
        <p className="text-xs text-muted-foreground mt-4">
          {t('common.timeline.generatingMayTakeTime')}
        </p>
      )}
    </div>
  );
}

